import React from "react";
import PropTypes from "prop-types";
import { BIBLIOGRAPHY_STEP_REFERENCE } from "./stepReference_bibliography";

const Step3Bibliography = ({
  formData,
  handleInputChange,
  onNext,
  onPrevious,
}) => {
  const handleBibliographyChange = (e) => {
    handleInputChange("bibliography_art", null, e.target.value);
  };

  // Fonction sécurisée pour compter les références
  const getReferenceCount = () => {
    try {
      // Vérifier que bibliography_art existe et est une chaîne
      const bibliography = formData.bibliography_art;

      if (!bibliography || typeof bibliography !== "string") {
        return 0;
      }

      return bibliography
        .split("\n")
        .filter((line) => line.trim() !== "" && /^\[\d+\]/.test(line.trim()))
        .length;
    } catch (error) {
      console.error("Error counting references:", {
        error: error.message,
        bibliographyType: typeof formData.bibliography_art,
      });
      return 0;
    }
  };

  // Fonction sécurisée pour obtenir la valeur de la bibliographie
  const getBibliographyValue = () => {
    const bibliography = formData.bibliography_art;

    if (!bibliography) {
      return "";
    }

    if (typeof bibliography === "string") {
      return bibliography;
    }

    // Si c'est un objet, essayer de le convertir
    if (typeof bibliography === "object") {
      try {
        return JSON.stringify(bibliography, null, 2);
      } catch (error) {
        console.warn("Cannot stringify bibliography object:", {
          error: error.message,
        });
        return "";
      }
    }

    // Conversion par défaut en chaîne
    return String(bibliography);
  };

  const referenceCount = getReferenceCount();
  const bibliographyValue = getBibliographyValue();

  // Gestionnaires de navigation avec logging
  const handleNext = () => {
    console.log("Bibliography step - Next clicked:", {
      hasContent: !!bibliographyValue,
      contentLength: bibliographyValue.length,
      referenceCount,
    });
    onNext();
  };

  const handlePrevious = () => {
    onPrevious();
  };

  return (
    <div className="step-content">
      {/* En-tête */}
      <div className="mb-6">
        <h3 className="mb-2 text-2xl font-bold text-gray-800">
          {BIBLIOGRAPHY_STEP_REFERENCE.stepTitle}
        </h3>
        <p className="text-gray-600">
          {BIBLIOGRAPHY_STEP_REFERENCE.stepDescription}
        </p>
      </div>

      {/* Exemple si vide */}
      {!bibliographyValue && (
        <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-6">
          <h4 className="mb-3 text-sm font-medium text-gray-700">
            💡 Exemple de bibliographie
          </h4>
          <div className="font-mono text-sm text-gray-600">
            <div className="mb-2">
              [1] Rakotomalala, A. (2023). Biodiversité marine de Madagascar.
              Revue Océanographique, 45(2), 123-145.
            </div>
            <div className="mb-2">
              [2] Andriamampianina, L. & Razafy, P. (2022). Techniques
              agroécologiques traditionnelles. Éditions Universitaires.
            </div>
            <div>
              [3] Randrianarivelo, M. (2021). Changement climatique et
              agriculture. Agricultural Research Madagascar, 8(1), 78-92.
            </div>
          </div>
        </div>
      )}
      {/* Champ de saisie */}
      <div className="mb-6">
        <div className="mb-3 flex items-center justify-between">
          <label className="block text-sm font-medium text-gray-700">
            {BIBLIOGRAPHY_STEP_REFERENCE.field.label}
          </label>
          {referenceCount > 0 && (
            <span className="text-sm text-gray-500">
              📚 {referenceCount} référence{referenceCount > 1 ? "s" : ""}
            </span>
          )}
        </div>

        <textarea
          name={BIBLIOGRAPHY_STEP_REFERENCE.field.name}
          value={bibliographyValue}
          onChange={handleBibliographyChange}
          placeholder={BIBLIOGRAPHY_STEP_REFERENCE.field.placeholder}
          rows={BIBLIOGRAPHY_STEP_REFERENCE.field.rows}
          maxLength={BIBLIOGRAPHY_STEP_REFERENCE.field.maxLength}
          className="w-full rounded-lg border border-gray-300 p-4 font-mono text-sm leading-relaxed focus:border-[#57A863] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
        />

        <p className="mt-2 text-xs text-gray-500">
          {BIBLIOGRAPHY_STEP_REFERENCE.field.helpText}
        </p>

        <div className="mt-2 flex items-center justify-between text-xs text-gray-400">
          <span>
            {bibliographyValue.length} /{" "}
            {BIBLIOGRAPHY_STEP_REFERENCE.field.maxLength} caractères
          </span>
        </div>
      </div>

      {/* Navigation */}
      <div className="flex justify-between">
        <button
          type="button"
          onClick={handlePrevious}
          className="flex items-center rounded-lg border border-gray-300 bg-white px-6 py-3 text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500/20"
        >
          <svg
            className="mr-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M15 19l-7-7 7-7"
            />
          </svg>
          {BIBLIOGRAPHY_STEP_REFERENCE.navigation.previous.label}
        </button>

        <button
          type="button"
          onClick={handleNext}
          className="flex items-center rounded-lg bg-[#57A863] px-6 py-3 text-white hover:bg-[#449954] focus:outline-none focus:ring-2 focus:ring-[#57A863]/20"
        >
          {BIBLIOGRAPHY_STEP_REFERENCE.navigation.next.label}
          <svg
            className="ml-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
};

Step3Bibliography.propTypes = {
  formData: PropTypes.shape({
    bibliography_art: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.object,
      PropTypes.array,
    ]),
  }).isRequired,
  handleInputChange: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
};

export default Step3Bibliography;
